import * as Sentry from "@sentry/react";
import { Divider, Form, Image, message } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomButton, CustomInput } from "../../components";
import { useLogin } from "../../context/LoginContext";
import localStorageKey from "../../enumerations/localStorageKey";
import { useSignIn } from "../../hooks/SignIn/signin.hooks";
import { usePostEmailSend } from "../../hooks/SignUp/signup.hooks";
import { useGetUserInfo } from "../../hooks/UserCenter/usercenter.hooks";
import styles from "./style.module.scss";

const Signin = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { setToken, setIsLogin } = useLogin();
  const [messageApi, contextHolder] = message.useMessage();
  const localToken = window.localStorage.getItem(localStorageKey.TOKEN);
  const localCart = window.localStorage.getItem(localStorageKey.CART);
  const { mutate: emailSend } = usePostEmailSend();
  const { mutate: signin } = useSignIn();
  const { data: userInfo, refetch } = useGetUserInfo({ enabled: !!localToken });
  const [isLoading, setIsLoading] = useState(false);
  const [messageShown, setMessageShown] = useState(false);
  const [values, setValues] = useState({
    account: "",
    password: "",
    confirm_password: "",
  });

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };
  const query = useQuery();

  const onFinish = async () => {
    setIsLoading(true);
    const field: any = {
      account: values.account,
      password: values.password,
    };
    signin(
      { params: field },
      {
        onSuccess: async (data: any) => {
          const token = data.data.data.token;
          const email = data.data.data.user.email;

          setToken(token);
          await refetch();

          window.localStorage.setItem(localStorageKey.USER_ACCOUNT, email);

          Sentry.setUser({
            id: data.data.data.user.account_id,
            username: email,
            email: email,
          });

          if (token) {
            setIsLogin(true);
            if (userInfo?.status === 0) {
              emailSend();
              navigate("/verify", { state: { fromLogin: true } });
            } else if (localCart && JSON.parse(localCart).length > 0) {
              navigate("/cart");
            } else {
              navigate("/");
            }
          }
          setIsLoading(false);
        },
        onError: (error: any) => {
          messageApi.open({
            type: "error",
            content: error,
          });
          console.debug(JSON.stringify("ERR", error));
          setIsLoading(false);
        },
      }
    );
  };

  const handleChange = (prop: any) => (event: any) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleClickLineLogin = () => {
    if (localCart && JSON.parse(localCart).length > 0) {
      localStorage.setItem(localStorageKey.HAVE_CART, "true");
    }
    window.location.replace(
      `${process.env.REACT_APP_API_USER_URL}/api/login/line`
    );
  };

  useEffect(() => {
    if (query) {
      const result = query.get("result");
      const error_msg = query.get("error");
      const token = query.get("token");
      const email = query.get("email");
      if (result === "true" && token && email) {
        localStorage.setItem(localStorageKey.TOKEN, token);
        localStorage.setItem(localStorageKey.USER_ACCOUNT, email);
        navigate("/");
      } else {
        if (!messageShown) {
          if (error_msg === "line_email") {
            message.error(t("line.email.error"));
            setMessageShown(true);
            return;
          } else if (error_msg === "line_login") {
            message.error(t("line.login.error"));
            setMessageShown(true);
            return;
          }
        }
      }
    }
  }, [query, messageShown]);

  return (
    <div className={styles.signinContainer}>
      {contextHolder}
      <div className={styles.signinTitle}>{t("normal.siginin")}</div>
      <div className={styles.signinWrapper}>
        <div className={styles.lineWrapper} onClick={handleClickLineLogin}>
          <Image
            width={25}
            height={25}
            src="/images/line.png"
            alt="line icon"
            preview={false}
          />
          <span>
            {t("line.text", {
              page: t("header.signin.signup"),
            })}
          </span>
        </div>
        <Divider />
        <div className={styles.inputContainer}>
          <Form
            form={form}
            name="siginin"
            initialValues={{}}
            autoComplete="off"
            requiredMark={false}
            layout="vertical"
            onFinish={onFinish}
          >
            <CustomInput
              formItemProps={{
                name: "account",
                label: t("normal.email"),
                rules: [
                  {
                    required: true,
                    message: t("form.required", { name: t("normal.email") }),
                  },
                  {
                    pattern:
                      /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/,
                    message: t("signup.emailFormat.error"),
                  },
                ],
              }}
              inputProps={{
                placeholder: t("signup.enterInput", { content: "" }),
                onChange: handleChange("account"),
              }}
            />
            <CustomInput
              formItemProps={{
                name: "password",
                label: t("normal.password"),
                rules: [
                  {
                    required: true,
                    message: t("form.required", { name: t("normal.password") }),
                  },
                  {
                    min: 6,
                    max: 18,
                    pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,18}$/,
                    message: t("normal.password.validation.error"),
                  },
                ],
              }}
              formItemType="password"
              inputProps={{
                placeholder: t("signup.enterInput", { content: "" }),
                onChange: handleChange("password"),
              }}
            />
            <CustomButton
              type="primary"
              size="large"
              htmlType="submit"
              disabled={isLoading}
              loading={isLoading}
              style={{
                width: "100%",
                marginTop: "29px",
              }}
            >
              {t("normal.siginin")}
            </CustomButton>
          </Form>
          <div className={styles.bottomGroup}>
            <div
              className={styles.bomttomBtn}
              onClick={() => {
                navigate("/forgotPassword");
              }}
            >
              {t("nornal.forgotPassword")}
            </div>
            {/* <div
              className={styles.bomttomBtn}
              onClick={() => {
                navigate("/signup");
              }}
            >
              {t("normal.register")}
            </div> */}
          </div>

          <CustomButton
            size="large"
            style={{
              width: "100%",
              marginTop: "32px",
            }}
            onClick={() => {
              navigate("/signup");
            }}
          >
            {t("signup.quickSignup")}
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default Signin;
